/**
 * Screen-local API module for the Custom Opt-in stats report (task 035-06).
 *
 * Codes against task 035-05's pinned contract
 * (`tasks/035_stats_screen/task_05_custom_optin_funnel_query_opus.md` §Contract,
 * lines 48-72). Task 05 need not have landed — this module and its test mock
 * the response shape; the DTOs below are the api-local (A3) types copied
 * verbatim from the pinned contract.
 *
 * Deliberately outside `lib/api.ts` (ADR-010 gap #2 -> A2 (b)): same base-URL
 * expression and `{ data }` envelope handling as `reports-api.ts` / `cto-stats-api.ts`.
 */

import type { CampaignStatus } from '@leads-platform/shared';
import { apiFetch } from '../../lib/api';

/**
 * One originating-site row of a Custom Opt-in campaign's lead drill-down
 * (pinned contract). `siteId` carries the RAW value — `''` for a lead with no
 * `site_id`; `(none)` is the SCREEN's label (task 06), never the DTO value —
 * the same rule as Paid Social's `utmSource`.
 */
export interface CustomOptinSiteRow {
  siteId: string;
  leads: number;
  uniqueLeads: number;
  rejected: number;
}

/** One campaign row of `GET /admin/reports/custom-optin-stats` (pinned contract). */
export interface CustomOptinRow {
  campaignId: string;
  campaignName: string;
  pageTitle: string | null;
  pageSlug: string | null;
  advertiserId: string;
  advertiserName: string;
  clientName: string | null;
  startDate: string | null; // yyyy-MM-dd or null
  endDate: string | null; // yyyy-MM-dd or null
  status: CampaignStatus;
  sendMode: 'click_out' | 'lead_post' | 'unknown';
  pricePerLead: number | null;
  limitType: 'total_clicks' | 'unique_leads' | 'unique_clicks' | null;
  limitValue: number | null;
  dailyCap: number | null;
  pageViews: number | null; // pages served: one per walk, page and day; null on coverage 'none'
  uniquePageViews: number | null; // distinct client IP over the same rows; null on coverage 'none'
  clicks: number; // accept POSTs
  uniqueClicks: number; // COUNT(DISTINCT ip_address)
  leads: number; // lead rows on the campaign
  uniqueLeads: number; // distinct email_hash
  billableUniqueLeads: number | null; // lead_post: CoopCountsService uniqueLeads; else null
  revenue: number | null; // click_out: clicks x price; lead_post: CoopCountsService revenue; unknown: null
  sites: CustomOptinSiteRow[]; // ORDER BY siteId
}

/** Column sums over the selection; `uniquePageViews` / `uniqueClicks` / `uniqueLeads` are distinct across it (non-additive). The two page-view cells are null on coverage `'none'`. */
export interface CustomOptinTotals {
  pageViews: number | null;
  uniquePageViews: number | null;
  clicks: number;
  uniqueClicks: number;
  leads: number;
  uniqueLeads: number;
  revenue: number | null;
}

export interface CustomOptinStatsResponse {
  from: string;
  to: string;
  timezone: string;
  includeTest: boolean;
  rows: CustomOptinRow[]; // ORDER BY advertiserName, campaignName, campaignId
  totals: CustomOptinTotals;
  pageViewsSince: string | null; // yyyy-MM-dd (reporting TZ) of the first recorded page view; null = none yet
  pageViewsCoverage: 'full' | 'partial' | 'none'; // none: no page-view data in range; partial: range contains pageViewsSince
}

/**
 * Screen filter state. `''` means the filter is off and is omitted from the
 * query string; `from`/`to`/`includeTest` are always sent (the co-op /
 * cto-stats convention). Query is `coopReportQuerySchema.pick({ from, to,
 * includeTest, advertiserId, campaignId, campaignStatus })` (pinned contract).
 */
export interface CustomOptinStatsFilters {
  from: string;
  to: string;
  includeTest: boolean;
  advertiserId: string;
  campaignId: string;
  campaignStatus: CampaignStatus | '';
}

/**
 * Task 07's endpoint (`campaign-leads.csv`) is live (the tail's last task):
 * a row whose export is routed there — `sendMode !== 'lead_post'`, i.e.
 * `click_out` and `unknown` — renders its `All` / `Unique` buttons.
 */
export const CAMPAIGN_LEADS_EXPORT_AVAILABLE = true;

/**
 * 007's `co-op-leads.csv` answers `variant=unique` since task 08 (deduplicated
 * billable set, attributed-campaign scope). Gates ONLY the `Unique` button on
 * a `lead_post` row (its `All` button always renders — that endpoint exists
 * regardless).
 */
export const COOP_UNIQUE_EXPORT_AVAILABLE = true;

export type CustomOptinLeadsVariant = 'all' | 'unique';

export interface CustomOptinLeadsExportParams {
  campaignId: string;
  from: string;
  to: string;
  includeTest: boolean;
  variant: CustomOptinLeadsVariant;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
const ADMIN_PREFIX = '/admin';

/** `GET /admin/reports/custom-optin-stats` query -- the pinned contract's `pick`. */
function buildCustomOptinStatsQuery(filters: CustomOptinStatsFilters): URLSearchParams {
  const params = new URLSearchParams();
  params.set('from', filters.from);
  params.set('to', filters.to);
  if (filters.advertiserId) params.set('advertiserId', filters.advertiserId);
  if (filters.campaignId) params.set('campaignId', filters.campaignId);
  if (filters.campaignStatus) params.set('campaignStatus', filters.campaignStatus);
  params.set('includeTest', filters.includeTest ? 'true' : 'false');
  return params;
}

/** Absolute Custom Opt-in stats URL, ready for `fetch`. */
export function customOptinStatsUrl(filters: CustomOptinStatsFilters): string {
  return `${API_BASE_URL}${ADMIN_PREFIX}/reports/custom-optin-stats?${buildCustomOptinStatsQuery(filters).toString()}`;
}

function buildLeadsExportQuery(params: CustomOptinLeadsExportParams): URLSearchParams {
  const query = new URLSearchParams();
  query.set('campaignId', params.campaignId);
  query.set('from', params.from);
  query.set('to', params.to);
  query.set('includeTest', params.includeTest ? 'true' : 'false');
  query.set('variant', params.variant);
  return query;
}

/**
 * Per-row export URL, chosen by `row.sendMode` (README decision 8 (a)/(b)):
 * `lead_post` rows use 007's `co-op-leads.csv`; `click_out` / `unknown` rows
 * use task 07's `campaign-leads.csv`.
 */
export function customOptinLeadsExportUrl(
  row: Pick<CustomOptinRow, 'campaignId' | 'sendMode'>,
  filters: Pick<CustomOptinStatsFilters, 'from' | 'to' | 'includeTest'>,
  variant: CustomOptinLeadsVariant
): string {
  const query = buildLeadsExportQuery({
    campaignId: row.campaignId,
    from: filters.from,
    to: filters.to,
    includeTest: filters.includeTest,
    variant,
  });
  const endpoint = row.sendMode === 'lead_post' ? 'co-op-leads.csv' : 'campaign-leads.csv';
  return `${API_BASE_URL}${ADMIN_PREFIX}/reports/${endpoint}?${query.toString()}`;
}

/**
 * `message` of a Nest error body without a cast (`reports-api.ts`'s idiom):
 * narrow the unknown JSON with the `in` operator, then the string check.
 */
function messageFromBody(body: unknown, fallback: string): string {
  if (typeof body === 'object' && body !== null && 'message' in body) {
    const message = body.message;
    if (typeof message === 'string' && message.length > 0) return message;
  }
  return fallback;
}

/** Success envelope -- matches the api's `ApiResponse<T>` (conventions §DTO). */
interface CustomOptinStatsEnvelope {
  data: CustomOptinStatsResponse;
}

/** Fetch the Custom Opt-in stats for one filter set. Non-2xx throws with the body's message (or the status text). */
export async function fetchCustomOptinStats(
  filters: CustomOptinStatsFilters
): Promise<CustomOptinStatsResponse> {
  const response = await apiFetch(customOptinStatsUrl(filters));
  if (!response.ok) {
    const body: unknown = await response.json().catch(() => ({}));
    throw new Error(messageFromBody(body, response.statusText));
  }
  const envelope: CustomOptinStatsEnvelope = await response.json();
  return envelope.data;
}
